import { useState, useEffect, useCallback } from 'react';
import {
  Notification,
  getNotifications,
  markRead,
  takeAction as takeActionApi,
} from '../services/notificationsService';

const POLL_INTERVAL_MS = 60_000;

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    try {
      const data = await getNotifications();
      setNotifications(data);
      setError(null);
    } catch (err) {
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
    const interval = setInterval(fetch, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetch]);

  const handleMarkRead = useCallback(async (notificationId: string) => {
    await markRead(notificationId);
    setNotifications(prev =>
      prev.map(n => n.notificationId === notificationId ? { ...n, read: true } : n)
    );
  }, []);

  const handleTakeAction = useCallback(async (notificationId: string, action: 'ignored' | 'purged') => {
    await takeActionApi(notificationId, action);
    setNotifications(prev =>
      prev.map(n =>
        n.notificationId === notificationId
          ? { ...n, actionTaken: action, actionTakenAt: new Date().toISOString(), read: true }
          : n
      )
    );
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  return {
    notifications,
    unreadCount,
    loading,
    error,
    refetch: fetch,
    markRead: handleMarkRead,
    takeAction: handleTakeAction,
  };
}
